import {
  ActionIcon,
  Alert,
  CopyButton,
  Divider,
  Drawer,
  Group,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { useOperation } from '../api/queries';
import { formatFullDateTime, formatKopecks, formatMoney } from '../lib/format';
import { KIND_COLOR } from '../theme';
import { CategoryBadge } from './CategoryBadge';
import { KindBadge } from './KindBadge';

interface OperationDetailsProps {
  operationId: string | null;
  onClose: () => void;
}

/** Details are re-fetched by id, so an operation removed via MCP shows as gone. */
export function OperationDetails({ operationId, onClose }: OperationDetailsProps) {
  const { data, isPending, error } = useOperation(operationId);

  return (
    <Drawer
      opened={operationId !== null}
      onClose={onClose}
      position="right"
      size="md"
      padding="lg"
      title={
        <Text fw={600}>{data?.title ?? 'Операция'}</Text>
      }
    >
      {error ? (
        <Alert color="red" variant="light" title="Операция недоступна">
          {error.message}
        </Alert>
      ) : isPending || !data ? (
        <Stack gap="md">
          <Skeleton height={44} radius="sm" />
          <Skeleton height={20} radius="sm" />
          <Skeleton height={20} radius="sm" />
        </Stack>
      ) : (
        <Stack gap="lg">
          <Stack gap={4}>
            <Text
              fz={32}
              fw={600}
              c={KIND_COLOR[data.kind]}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatMoney(data.signed_amount, { withSign: true })}
            </Text>
            <Group gap="xs">
              <KindBadge kind={data.kind} />
              <Text size="xs" c="dimmed">
                {formatKopecks(data.amount)} · {data.currency}
              </Text>
            </Group>
          </Stack>

          <Divider />

          <Stack gap="md">
            {data.description && <Field label="Описание">{data.description}</Field>}

            <Field label="Категория">
              <CategoryBadge category={data.category} />
            </Field>

            <Field label="Дата и время">{formatFullDateTime(data.occurred_at)}</Field>

            <Field label="Идентификатор">
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>
                  {data.id}
                </Text>
                <CopyIdButton value={data.id} />
              </Group>
            </Field>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      {typeof children === 'string' ? <Text size="sm">{children}</Text> : children}
    </Stack>
  );
}

function CopyIdButton({ value }: { value: string }) {
  return (
    <CopyButton value={value} timeout={1500}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? 'Скопировано' : 'Скопировать id'} withArrow>
          <ActionIcon
            variant="subtle"
            color={copied ? 'teal' : 'gray'}
            aria-label="Скопировать идентификатор"
            onClick={copy}
          >
            {copied ? <IconCheck size={16} stroke={1.5} /> : <IconCopy size={16} stroke={1.5} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}
